// Typed client for recording ONE tournament or series result (#404 / PR 2).
//
// Until this, the portal applied the result in the browser and wrote the
// club's whole session document back through /kv. That is one rule with two
// implementations — the pad has had a server-side path since #183 — and the
// browser's had no exactly-once boundary at all, because a blob write cannot
// have one. A double-tap, a retry after a dropped answer, two tabs: each one
// applied the ELO again.
//
// On the API backend the server owns it: it reads the session, runs the same
// engine applier, claims the match in `result_commits`, commits the players'
// global ratings and writes the document back — all in one transaction. The
// browser sends an id and a score and waits for the answer.
//
// In memory there is no server to own anything, so the caller keeps its
// existing local path. `resultsAreServerAuthoritative()` is the switch, and it
// is the same shape `courts.ts` uses for the court-state command (#271).
import { Storage } from '../storage';
import { apiFetch } from './http';

/**
 * How long to wait for the server before giving the operator an answer.
 *
 * A REQUEST THAT NEVER ANSWERS IS WORSE THAN A REFUSAL, and this one is easy
 * to hit: an API restart during a deploy leaves an in-flight request hanging,
 * and because the handlers guard against a second submission while one is in
 * flight, the modal simply stops responding. That is what a dev deploy looked
 * like from the operator's side — a frozen dialog with nothing to do but
 * reload, and no idea whether the result had landed.
 *
 * TIMING OUT A WRITE IS NORMALLY DANGEROUS: aborting does not undo it, so the
 * caller cannot know whether it applied. It is safe HERE, and only because of
 * the exactly-once work — a retry of the same result comes back `replayed`
 * carrying the stored session and version, so trying again after a timeout
 * either records it or recovers it. That is why this bound lives on this
 * endpoint rather than in `apiFetch`, where the same abort would leave other
 * writes genuinely ambiguous.
 */
const RESULT_TIMEOUT_MS = 15_000;

/** A bounded wait, so a hung request becomes an answer rather than a freeze. */
const withTimeout = (): RequestInit =>
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? { signal: AbortSignal.timeout(RESULT_TIMEOUT_MS) }
    : {};

/** What the operator is told when the server never answered. */
const TIMED_OUT =
  'Serveren svarte ikke. Resultatet kan ha blitt registrert — prøv igjen, det blir ikke lagt inn to ganger.';

/** True when the result is applied by the server rather than by this browser. */
export const resultsAreServerAuthoritative = () => Storage.backend === 'http';

/** A refusal the USER needs to understand, as opposed to a fault. */
export class ResultRefused extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ResultRefused';
  }
}

export interface RecordResultInput {
  /** The tournament's or series' own id — the server finds where it lives. */
  sessionId: string;
  matchId: string;
  /** KM and seriespill carry a structured score; the rest carry two numbers. */
  score?: unknown;
  score1?: number | null;
  score2?: number | null;
}

export interface RecordedResult {
  /**
   * The result was already recorded — and this response RECOVERS it.
   *
   * Not merely «you need not try again». A request can commit and lose its
   * response, so a browser retrying is still showing the match as unplayed and
   * holding a version from before a write it never heard about. A replay
   * therefore carries `session` and `storage` exactly as a fresh application
   * does, and the caller adopts both.
   *
   * An ABSENT session still means adopt nothing: blanking a tournament because
   * a field was missing is worse than failing to recover.
   */
  replayed: boolean;
  format: string;
  /** The session AFTER the result. Absent on a replay. */
  session?: any;
  /**
   * WHERE the server wrote, and at which version.
   *
   * Handed to the caller rather than adopted here, because for a PAUSED
   * tournament the row holds the whole LIST and this module has only the one
   * session. `adoptServerWrite` insists on the document that was stored, and
   * the state layer is the only place that has it. Absent on a replay, which
   * wrote nothing.
   */
  storage?: { key: string; version: number };
}

/** What the user is told. Norwegian, because it reaches the screen. */
const MESSAGES: Record<string, string> = {
  already_finalised:
    'Kampen er allerede registrert. Endring av et ferdig resultat er ikke mulig her.',
  session_not_found: 'Fant ikke turneringen eller serien. Last siden på nytt.',
  tournament_not_running: 'Turneringen er ikke startet.',
  match_not_found: 'Fant ikke kampen i turneringen.',
  score_required: 'Resultatet mangler.',
  unsupported_format: 'Dette turneringsformatet kan ikke registreres her.',
  unknown_players: 'En eller flere spillere finnes ikke i spillerbasen.',
  class_not_found: 'Fant ikke klassen i serien.',
  duplicate_players: 'Samme spiller er med flere ganger i kampen.',
};

/**
 * Record a result and WAIT for the server to have applied it.
 *
 * Deliberately not optimistic. The old path updated the screen first and wrote
 * afterwards, so a refused or lost write left a match looking played that the
 * database says was not — and the next reload silently undid it in front of
 * whoever entered it.
 */
export async function recordTournamentResult(input: RecordResultInput): Promise<RecordedResult> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch('/tournament-results', {
      ...withTimeout(),
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch (e) {
    // An abort or a dropped connection. The message says a retry is safe,
    // because the claim makes it so.
    if ((e as { name?: string })?.name === 'TimeoutError') {
      throw new ResultRefused('timed_out', TIMED_OUT);
    }
    throw e;
  }

  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    const code = detail?.error || `http_${res.status}`;
    if (res.status === 403) {
      throw new ResultRefused('forbidden', 'Du har ikke tilgang til å registrere resultater.');
    }
    throw new ResultRefused(
      code,
      MESSAGES[code] || `Kunne ikke registrere resultatet (${res.status})`,
    );
  }

  const body = (await res.json()) as {
    replayed?: boolean;
    format?: string;
    session?: any;
    storage?: { key?: string; version?: number };
  };

  return {
    replayed: body.replayed === true,
    format: String(body.format ?? ''),
    session: body.session,
    storage:
      typeof body.storage?.key === 'string' && typeof body.storage?.version === 'number'
        ? { key: body.storage.key, version: body.storage.version }
        : undefined,
  };
}

/**
 * Settle everything a legacy KM/seriespill session still owes, once.
 *
 * Called before ARCHIVING one. Since #405 a result commits as it is recorded,
 * but a session from before that transition is holding contributions no
 * player's rating has received — and archive no longer commits them, so this
 * is what stops them being lost.
 *
 * Idempotent: the server claims every match it pays for, so a session with
 * nothing outstanding is a no-op. The caller does not need to know which it is.
 */
export async function catchUpSessionRatings(sessionId: string): Promise<{ caughtUp: number }> {
  const res = await apiFetch('/tournament-results/catch-up', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ResultRefused(
      detail?.error || `http_${res.status}`,
      MESSAGES[detail?.error ?? ''] || `Kunne ikke gjøre opp turneringens ELO (${res.status})`,
    );
  }
  const body = (await res.json()) as { caughtUp?: number };
  return { caughtUp: Number(body.caughtUp ?? 0) };
}

/**
 * Start a seriespill class's next season, server-side.
 *
 * The browser used to commit the class's ELO and then rewrite the series
 * document as two separate steps. A failure between them left the finished
 * season's matches live AND already paid for, which a later catch-up would pay
 * for again. The server does both in one transaction (#405).
 */
export async function startSeriesNextSeason(input: {
  sessionId: string;
  classId: string;
  assignments?: unknown;
  newSeasonStart?: unknown;
  newDeadline?: unknown;
  newPairs?: unknown;
}): Promise<{ series: any; caughtUp: number; storage?: { key: string; version: number } }> {
  const res = await apiFetch('/season-transition', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    const code = detail?.error || `http_${res.status}`;
    throw new ResultRefused(
      code,
      MESSAGES[code] || `Kunne ikke starte neste sesong (${res.status})`,
    );
  }
  const body = (await res.json()) as {
    series?: any;
    caughtUp?: number;
    storage?: { key?: string; version?: number };
  };
  return {
    series: body.series,
    caughtUp: Number(body.caughtUp ?? 0),
    // WHERE THE SERVER WROTE, AND AT WHICH VERSION. Dropping this is not
    // cosmetic: the browser still holds a version token for
    // `active_series_v1` from before the transition, so its next ordinary
    // write carries a stale `ifVersion` and is refused — for a change it
    // asked the server to make. Same rule as `recordTournamentResult` above,
    // including validating both halves rather than adopting one of them.
    storage:
      typeof body.storage?.key === 'string' && typeof body.storage?.version === 'number'
        ? { key: body.storage.key, version: body.storage.version }
        : undefined,
  };
}
